import { useEffect } from "react";
import {
	BrowserRouter as Router,
	Navigate,
	Route,
	Routes,
	useLocation,
} from "react-router-dom";
import "./App.css";
import { Sidebar } from "./components";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useCatalogIndex } from "./hooks/useCatalogIndex";
import { ProjectProvider } from "./context/ProjectContext";
import { ToastProvider } from "./context/ToastContext";
import { UploadManagerProvider } from "./context/UploadManager";
import { AddFilesProvider } from "./context/AddFilesContext";
import { WorkflowRunNotifierProvider } from "./context/WorkflowRunNotifier";
import { isNewDashboardTemplate } from "./components/Dashboards/NewDashboardModal";
import RedirectToPath from "./components/RedirectToPath";
import UploadTray from "./components/Upload/UploadTray";
import WorkflowRunToast from "./components/Workflows/WorkflowRunToast";
import { DashboardsIndexProvider } from "./context/DashboardsIndex";
import KpiDashboardPage from "./pages/dashboards/KpiDashboardPage";
import {
	AuthCallback,
	Cabinet,
	Chat,
	ContextGraph,
	Dashboards,
	DealScreeningRunPage,
	DealScreeningWorkflowPage,
	DevCitationPreview,
	ForgotPassword,
	LoanTapeDashboardPage,
	NewLoanTapePage,
	PortfolioDataPage,
	Login,
	MonitoringRunPage,
	MonitoringTemplatePage,
	NewSheet,
	NotFound,
	ResetPassword,
	Settings,
	Signup,
	Upload,
} from "./pages";
import OAuthCallback from "./pages/OAuthCallback";
import DealFiles from "./pages/files/DealFiles";
import FileDetail from "./pages/files/FileDetail";
import FilesHome from "./pages/files/FilesHome";
import { SupportEnded, SupportEnter } from "./pages/SupportGate";
import SupportSessionBanner from "./components/SupportSessionBanner";
import { purgeLegacyProviderTokens } from "./services/integrationApi";
import {
	isCabinetUiEnabled,
	isDealScreeningEnabled,
	isMonitoringEnabled,
	isWorkflowsEnabled,
} from "./utils/featureFlags";
import { RETURN_TO_PARAM, safeReturnPath } from "./utils/sessionExpiry";

/**
 * `?compose=extract_kpis` opened the Templates page's composer; the same intent
 * is now "open the New workflow modal on that template". An unknown value is
 * dropped rather than passed through — it would open the picker anyway, and a
 * `?template=` naming nothing is a worse thing to leave in a bookmark.
 */
function composeToNewDashboard(search: URLSearchParams): URLSearchParams {
	const compose = search.get("compose");
	search.delete("compose");
	if (compose) {
		search.set("new", "1");
		if (isNewDashboardTemplate(compose)) search.set("template", compose);
	}
	return search;
}

function AppContent() {
	const { isAuthenticated } = useAuth();
	useCatalogIndex();
	// Read at render so a deployment can flip it without a rebuild.
	const workflowsEnabled = isWorkflowsEnabled();

	useEffect(() => {
		// Evict provider tokens older builds left in localStorage.
		purgeLegacyProviderTokens();

		// Handle OAuth callback if current URL is a callback
		const currentPath = window.location.pathname;
		if (currentPath.includes("/auth/") && currentPath.includes("/callback")) {
			// Force React Router to handle this route
			window.history.replaceState({}, "", window.location.href);
		}
	}, []);

	return (
		<Routes>
			{/* Public routes */}
			{/* An already-authenticated visit to /login still honours `?next=`, so a
			    stale 401 that bounced a live session lands back where it started. */}
			<Route
				path="/login"
				element={
					isAuthenticated ? (
						<Navigate
							to={
								safeReturnPath(
									new URLSearchParams(window.location.search).get(
										RETURN_TO_PARAM,
									),
								) ?? "/upload"
							}
							replace
						/>
					) : (
						<Login />
					)
				}
			/>
			<Route
				path="/signup"
				element={isAuthenticated ? <Navigate to="/upload" /> : <Signup />}
			/>
			<Route path="/forgot-password" element={<ForgotPassword />} />
			<Route path="/reset-password" element={<ResetPassword />} />
			<Route path="/auth/google/callback" element={<AuthCallback />} />
			<Route path="/auth/box/callback" element={<OAuthCallback />} />
			<Route path="/auth/dropbox/callback" element={<OAuthCallback />} />
			<Route path="/auth/sharepoint/callback" element={<OAuthCallback />} />
			{/* Support sessions (DOP-562): reached only from the internal console. */}
			<Route path="/support/enter" element={<SupportEnter />} />
			<Route path="/support/ended" element={<SupportEnded />} />

			{/* Protected routes */}
			<Route
				path="/*"
				element={
					<ProtectedRoute>
						{/* The upload manager sits ABOVE the inner <Routes>, so a folder
						    upload started on /upload keeps running — and stays visible in
						    the tray — after the user is sent to /chat. Mounting it inside
						    a page would tie the job's lifetime to that page's mount. */}
						<UploadManagerProvider>
						{/* Adding files to an existing deal (DOP-608) opens one
						    review dialog from the sidebar and from Data Sources, and
						    hands the upload to the manager above. */}
						<AddFilesProvider>
							{/* Same reason, for workflow runs: an extraction takes
							    minutes, so the watch that reports its outcome has to
							    outlive /templates. Held here, the completion pop-up
							    reaches the user wherever they navigated to. */}
							<WorkflowRunNotifierProvider>
								{/* One read of the workflow list for the whole app: the
								    index lists them and the "New workflow" modal refreshes
								    that list, and two hook instances would put two identical
								    requests on the wire per visit. */}
								<DashboardsIndexProvider>
								<div className="flex h-screen bg-gray-50">
									<div>
										<Sidebar />
									</div>
									{/* min-w-0: a flex item's default min-width is its CONTENT
									    width, so a page holding anything wider than the viewport
									    (the KPI grid at a wide period range) stretches this column
									    and scrolls the whole layout sideways — sidebar and all.
									    With it, wide content has to scroll inside its own
									    container, which is where the scrollbar belongs. */}
									<div className="flex-1 flex flex-col min-w-0 overflow-hidden">
										<Routes>
											<Route path="/" element={<Navigate to="/upload" />} />
											<Route path="/upload" element={<Upload />} />
											{/* The whole workflow section — index, every workflow page and
											    every retired path that redirects into it — answers to one
											    flag (DOP-483). A tenant that opts out gets NotFound on every
											    door in, not a section with nothing to run. */}
											{workflowsEnabled && (
												<>
											{/* One noun (DOP-478, named by DOP-485). A workflow is
											    a configured template plus its output plus its run
											    history, so the index lists them and each has a page
											    of its own. The KPI (DOP-479), monitoring (DOP-480)
											    and loan tape (DOP-481) each fill the shell with
											    their own body. */}
											<Route path="/workflows" element={<Dashboards />} />
											<Route
												path="/workflows/kpi/:workflowId"
												element={<KpiDashboardPage />}
											/>
											<Route
												path="/workflows/loan-tape/new"
												element={<NewLoanTapePage />}
											/>
											<Route
												path="/workflows/loan-tape/:id"
												element={<LoanTapeDashboardPage />}
											/>
											<Route
												path="/workflows/portfolio-data/:deal"
												element={<PortfolioDataPage />}
											/>
											{/* Every path the unified IA retires, redirecting with
											    whatever the old link carried in its query string.
											    `/workflows*` is included: it was the epic's own
											    first name for this section and links to it are
											    already out in shared chats and bookmarks. */}
											<Route
												path="/dashboards"
												element={<RedirectToPath to="/workflows" />}
											/>
											<Route
												path="/dashboards/kpi/:workflowId"
												element={<RedirectToPath to="/workflows/kpi/:workflowId" />}
											/>
											<Route
												path="/dashboards/loan-tape/:id"
												element={<RedirectToPath to="/workflows/loan-tape/:id" />}
											/>
											<Route
												path="/dashboards/monitoring/runs/:runId"
												element={
													<RedirectToPath to="/workflows/monitoring/runs/:runId" />
												}
											/>
											<Route
												path="/dashboards/monitoring/:templateId"
												element={
													<RedirectToPath to="/workflows/monitoring/:templateId" />
												}
											/>
											<Route
												path="/dashboard"
												element={<RedirectToPath to="/workflows" />}
											/>
											<Route
												path="/kpi"
												element={<RedirectToPath to="/workflows" />}
											/>
												</>
											)}
											<Route path="/chat/:chatId?" element={<Chat />} />
											<Route path="/new-sheet" element={<NewSheet />} />
											{/* Account page: name, password, sign out. The sidebar
											    gear used to open a Logout modal and nothing else. */}
											<Route path="/settings" element={<Settings />} />
											{workflowsEnabled && (
												<>
											{/* Templates stopped being a place: picking one is a
											    step inside "New workflow", so `?compose=` becomes
											    the modal's `?new=1&template=`. */}
											<Route
												path="/templates"
												element={
													<RedirectToPath
														to="/workflows"
														mapSearch={composeToNewDashboard}
													/>
												}
											/>
												</>
											)}
											{/* Cabinet v1 catalog graph. Gated with the sidebar
											    entry so the surface is all-or-nothing; with the
											    flag off the path falls through to NotFound. */}
											{/* The Files screens: every deal's files, one file, and
											    Connect a source. */}
											<Route path="/files" element={<FilesHome />} />
											<Route
												path="/files/deals/:dealId"
												element={<DealFiles />}
											/>
											<Route
												path="/files/file/:fileId"
												element={<FileDetail />}
											/>
											{isCabinetUiEnabled() && (
												<Route path="/cabinet" element={<Cabinet />} />
											)}
											{/* The same catalog seen as the graph an answer was
											    reasoned over — one flag, because it is one surface
											    behind two routes. */}
											{isCabinetUiEnabled() && (
												<Route
													path="/context-graph"
													element={<ContextGraph />}
												/>
											)}
											{/* Portfolio monitoring (DOP-451 template page, DOP-452 run page).
											    Gated exactly like /cabinet: with the flag off both paths fall
											    through to NotFound rather than rendering a dead page. */}
											{workflowsEnabled && isMonitoringEnabled() && (
												<Route
													path="/workflows/monitoring/runs/:runId"
													element={<MonitoringRunPage />}
												/>
											)}
											{workflowsEnabled && isMonitoringEnabled() && (
												<Route
													path="/workflows/monitoring/:templateId"
													element={<MonitoringTemplatePage />}
												/>
											)}
											{/* Deal screening (DOP-497): the workflow page and one deal's
											    memo. Inside the Workflows gate and behind its own flag, so a
											    tenant without it gets NotFound on both doors. */}
											{workflowsEnabled && isDealScreeningEnabled() && (
												<Route
													path="/workflows/deal-screening/runs/:runId"
													element={<DealScreeningRunPage />}
												/>
											)}
											{workflowsEnabled && isDealScreeningEnabled() && (
												<Route
													path="/workflows/deal-screening/:workflowId"
													element={<DealScreeningWorkflowPage />}
												/>
											)}
											{workflowsEnabled && (
												<>
											{/* The paths monitoring shipped on. */}
											<Route
												path="/templates/monitoring/runs/:runId"
												element={
													<RedirectToPath to="/workflows/monitoring/runs/:runId" />
												}
											/>
											<Route
												path="/templates/monitoring/:templateId"
												element={
													<RedirectToPath to="/workflows/monitoring/:templateId" />
												}
											/>
												</>
											)}
											{/* Retired: connection state now lives in the "Connect to
											    Data Room" dialog on /upload. The page component is kept
											    so this route can be restored by uncommenting. */}
											{/* <Route
												path="/settings/data-sources"
												element={<DataSources />}
											/> */}
											<Route
												path="/dev/citations"
												element={<DevCitationPreview />}
											/>
											<Route path="*" element={<NotFound />} />
										</Routes>
									</div>
									<UploadTray />
									{workflowsEnabled && <WorkflowRunToast />}
								</div>
								</DashboardsIndexProvider>
								</WorkflowRunNotifierProvider>
						</AddFilesProvider>
						</UploadManagerProvider>
					</ProtectedRoute>
				}
			/>
		</Routes>
	);
}

/** The banner, everywhere except the two pages that open and close a session. */
function SupportSessionMark() {
	const { pathname } = useLocation();
	if (pathname.startsWith("/support/")) return null;
	return <SupportSessionBanner />;
}

function App() {
	return (
		<Router>
			<AuthProvider>
				<ProjectProvider>
					{/* Outermost notice channel: a failure on any page has somewhere
					    to be said now that `alert()` is gone. */}
					<ToastProvider>
						<SupportSessionMark />
						<AppContent />
					</ToastProvider>
				</ProjectProvider>
			</AuthProvider>
		</Router>
	);
}

export default App;
